/**
 * useScoreHighlight
 *
 * Markierungs-Teil der interaktiven Vorschau: Takt-Cursor (cursors[1] Auswahl, cursors[2] Hover),
 * Note-Cursor (cursors[0], grün), das temporäre SVG-Farb-Highlight in VIER Layern (unspielbare
 * Noten `UNPLAYABLE_COLOR` zuunterst, Wiederholungen der Auswahl `PATTERN_COLOR` darüber,
 * persistente Auswahl `SELECTION_COLOR` darüber, transienter Hover `HOVER_COLOR` obenauf) sowie
 * die Pixel-Position der Auswahl für die schwebenden Editoren.
 * Sub-Composable von
 * {@link @/composables/useOsmdInteractiveScore}.
 */
import type {Ref} from 'vue';
import {
    GraphicalMeasure,
    GraphicalNote,
    OpenSheetMusicDisplay,
    unitInPixels,
    VexFlowGraphicalNote,
} from 'opensheetmusicdisplay';
import {selectionTargetElements} from './highlightLogic';
import {collectPageSvgs, pageIndexOfNote, sheetUnitsToClient} from './pageSvgs';

export interface ScoreHighlightDeps {
    getOsmd: () => OpenSheetMusicDisplay | null;
    /** OSMD-Render-Div (Koordinaten-Referenz der Partitur). */
    containerRef: Ref<HTMLElement | null>;
    /** Scrollender Body-Container (Bezug von `selectedNoteX/Y`). */
    scrollContainerRef: Ref<HTMLElement | null>;
    /** Angeklickte Note (Quelle für Highlight-Farbe + Panel-Position). */
    clickedGraphicalNote: Ref<GraphicalNote | null>;
    /** Pixel-Position der Auswahl relativ zum Body-Container; `null` = keine Note. */
    selectedNoteX: Ref<number | null>;
    selectedNoteY: Ref<number | null>;
    /** Wiederholungen der Auswahl im Stück (`patternMatch.findPatternMatches`); fehlt sie, bleibt
     *  der Pattern-Layer leer. */
    findPatternMatches?: (selected: GraphicalNote[]) => GraphicalNote[];
}

// Temporäres (nicht-persistentes) Auswahl-Highlight: die ausgewählten Noten werden direkt im SVG
// magenta eingefärbt – zusätzlich zum grünen Standard-Note-Cursor (cursors[0]). Wird NICHT
// ins MusicXML geschrieben. Magenta hebt sich bewusst vom blauen CurrentArea-Takt-Cursor
// (cursors[1]) ab.
export const SELECTION_COLOR = 'rgb(239, 4, 255)';
// Hover-Vorschau (transient, liegt IMMER über dem Auswahl-Layer): deutlich hellere Magenta-Tönung,
// damit gehoverte und ausgewählte Noten unterscheidbar bleiben.
export const HOVER_COLOR = '#F59BFA';
// Noten, die auf der Harmonika nicht existieren. Bewusst DASSELBE Rot, das die Engine den
// NotFound-Pausen der Griffschrift-Ausgabe gibt (`griffschrift/chords/creation.py`) – beide
// Ansichten sollen dieselbe Sprache sprechen: rot hier = rote Pause dort.
export const UNPLAYABLE_COLOR = '#ff0000';
// Stellen, an denen sich das ausgewählte Pattern wiederholt (Regeln in `patternMatch.ts`). Cyan,
// weil Rot, Magenta/Rosa, das Cursor-Grün, das Grün der Auto-Akkorde und das Dunkelblau der
// Bass-Handarbeit schon vergeben sind.
export const PATTERN_COLOR = '#0891B2';

/** Gemerkter Original-inline-Style eines gefärbten SVG-Elements (`''` = keine inline-Property). */
interface ColoredNode {
    el: SVGElement;
    fill: string;
    stroke: string;
}

export function useScoreHighlight(deps: ScoreHighlightDeps) {
    const {
        getOsmd, containerRef, scrollContainerRef, clickedGraphicalNote, selectedNoteX, selectedNoteY,
        findPatternMatches,
    } = deps;

    // Vier getrennte Restore-Stacks (kritisch – nicht zusammenlegen): der Unspielbar-Layer liegt
    // zuunterst und ist persistent, darüber der Pattern-Layer (folgt der Auswahl), darüber der
    // persistente Auswahl-Layer, obenauf der transiente Hover-Layer. Invariante: JEDE Änderung am
    // Auswahl-Layer räumt zuerst den Hover-Layer ab – sonst würde ein späteres `clearHoverColor()`
    // die im Hover-Stack als „Original" gemerkte Auswahlfarbe zurückschreiben (stale Magenta).
    // Abgeräumt wird immer von oben nach unten (Hover → Auswahl → Pattern), gefärbt von unten nach
    // oben – nur so merkt sich jeder Stack die Farbe des Layers darunter als „Original".
    //
    // Die Layer-REIHENFOLGE ist tragend (kritisch – nicht umdrehen): `applyUnplayableColor` läuft
    // nach jedem Render VOR `restoreSelectionHighlight()`. Nur dann merkt sich der Auswahl-Stack
    // das Rot als „Original" und gibt es beim Abwählen zurück. Andersherum – Rot über eine bereits
    // aktive Auswahl gelegt – merkte sich der Rot-Stack das Magenta, und `clearSelectionColor()`
    // schriebe die falsche Farbe zurück.
    let selectionColoredNodes: ColoredNode[] = [];
    let hoverColoredNodes: ColoredNode[] = [];
    let patternColoredNodes: ColoredNode[] = [];
    let unplayableColoredNodes: ColoredNode[] = [];

    function highlightMeasure(measure: GraphicalMeasure, hoverCursor: boolean = false): void {
        const osmd = getOsmd();
        const cursor = osmd?.cursors?.[hoverCursor ? 2 : 1];
        if (!osmd || !cursor) return;
        // Direkter Iterator-Sprung auf den Takt-Anfang. KEIN `cursor.reset()` + Scan: reset()
        // startet den Iterator bei `Sheet.SelectionStart`, den jeder Notenklick (Playback-Start)
        // auf die zuletzt gewählte Note verschiebt – eine Rückwärts-Navigation fände ihr Ziel
        // dann nie. `getIterator(ts)` nimmt den Sheet-Timestamp (`AbsoluteTimestamp`, bei
        // Wiederholungen der erste Durchlauf – graphisch derselbe Takt) und `show()` → `update()`
        // positioniert visuell; das per-Cursor `follow: false` bleibt dabei wirksam.
        cursor.iterator = osmd.Sheet.MusicPartManager.getIterator(measure.parentSourceMeasure.AbsoluteTimestamp);
        cursor.show();
    }

    /** Versteckt beide Takt-Cursor (Auswahl cursors[1] UND Hover cursors[2]). Der Hover-Cursor
     *  gehört mit dazu: er wurde früher nirgends versteckt und blieb beim Verlassen des
     *  Notenbilds ohne aktive Auswahl sichtbar stehen. */
    function clearHighlight(): void {
        const cursors = getOsmd()?.cursors;
        cursors?.[1]?.hide();
        cursors?.[2]?.hide();
    }

    /** Setzt gefärbte Elemente auf ihren gemerkten Original-inline-Style zurück. Nach einem
     *  Re-Render sind die Alt-Elemente detached (`isConnected === false`) → einfach überspringen.
     *  Ein leerer inline-Wert (`''`) entfernt die Property → das ursprüngliche Präsentationsattribut
     *  bzw. der Default greift wieder. `fill`/`stroke` werden 1:1 (NICHT über Kreuz) restauriert. */
    function restoreNodes(nodes: ColoredNode[]): void {
        for (const {el, fill, stroke} of nodes) {
            if (!el.isConnected) continue;
            el.style.fill = fill;
            el.style.stroke = stroke;
        }
    }

    /** Entfernt den transienten Hover-Layer; der Auswahl-Layer bleibt unberührt. */
    function clearHoverColor(): void {
        restoreNodes(hoverColoredNodes);
        hoverColoredNodes = [];
    }

    /** Entfernt das Auswahl-Highlight samt dem darüberliegenden Hover-Layer und dem darunter
     *  liegenden Pattern-Layer (der zur Auswahl gehört) – in dieser Reihenfolge. */
    function clearSelectionColor(): void {
        clearHoverColor();
        restoreNodes(selectionColoredNodes);
        selectionColoredNodes = [];
        restoreNodes(patternColoredNodes);
        patternColoredNodes = [];
    }

    /** Färbt die SVG-Elemente einer Note und merkt die Originale im übergebenen Layer-Stack. Nutzt
     *  die typisierte OSMD-API `VexFlowGraphicalNote.getSVGGElement()`; bei Nicht-SVG-Backend/
     *  fehlender Methode passiert nichts. Setzt die Farbe per inline-`style` (wie die offizielle
     *  OSMD-Demo) – gewinnt gegen etwaige VexFlow-inline-styles, was ein reines
     *  `setAttribute('fill')` nicht täte. Bei Akkorden teilen sich alle Töne dieselbe StaveNote-`<g>`,
     *  daher wird über `vfnoteIndex` nur der Kopf der betroffenen Note gefärbt (siehe
     *  {@link selectionTargetElements}) – sonst würden alle Akkordköpfe gefärbt. */
    function colorNote(note: GraphicalNote, color: string, layer: ColoredNode[]): void {
        try {
            const vfNote = note as VexFlowGraphicalNote;
            const g = vfNote.getSVGGElement?.();
            if (!g) return;
            for (const el of selectionTargetElements(g, vfNote.vfnoteIndex)) {
                // Stroke nur einfärben, wenn das Element überhaupt einen hatte – sonst bekämen
                // strichlose (nur gefüllte) Elemente eine ungewollte farbige Kontur.
                const stroke = el.style.stroke || el.getAttribute('stroke');
                const hadStroke = !!stroke && stroke !== 'none';
                layer.push({el, fill: el.style.fill, stroke: el.style.stroke});
                el.style.fill = color;
                if (hadStroke) el.style.stroke = color;
            }
        } catch (e) {
            console.warn('[OSMD] colorNote failed', e);
        }
    }

    /** Entfernt die Unspielbar-Markierung (unterster Layer). */
    function clearUnplayableColor(): void {
        restoreNodes(unplayableColoredNodes);
        unplayableColoredNodes = [];
    }

    /** Markiert Noten, die auf der Harmonika nicht existieren, im untersten (persistenten) Layer.
     *  Ersetzt eine vorherige Markierung. Nach einem Re-Render sind die alten Knoten detached –
     *  `restoreNodes` überspringt sie, der Aufruf ist also auch dann gefahrlos. Muss VOR dem
     *  Wiederherstellen des Auswahl-Highlights laufen (siehe Layer-Reihenfolge oben). */
    function highlightUnplayableNotes(notes: GraphicalNote[]): void {
        clearUnplayableColor();
        for (const n of notes) colorNote(n, UNPLAYABLE_COLOR, unplayableColoredNodes);
    }

    /** Färbt die angeklickte Note im Auswahl-Layer (temporäres Auswahl-Highlight). */
    function applySelectionColor(note: GraphicalNote): void {
        colorNote(note, SELECTION_COLOR, selectionColoredNodes);
    }

    /** Transiente Hover-Vorschau: ersetzt den vorherigen Hover-Layer und färbt `notes` in
     *  `HOVER_COLOR`. Berührt weder den Auswahl-Layer noch den grünen Note-Cursor – eine gehoverte,
     *  bereits ausgewählte Note merkt sich ihre Auswahlfarbe als Original und bekommt sie bei
     *  {@link clearHoverColor} zurück. */
    function highlightHoverNotes(notes: GraphicalNote[]): void {
        clearHoverColor();
        for (const n of notes) colorNote(n, HOVER_COLOR, hoverColoredNodes);
    }

    /** Positioniert den grünen Note-Cursor (cursors[0]) exakt auf `note` (oder versteckt ihn bei
     *  `null`). Direkter Iterator-Sprung auf den Timestamp der Note – kein `reset()` + Scan
     *  (Begründung siehe {@link highlightMeasure}). */
    function positionNoteCursor(note: GraphicalNote | null): void {
        const osmd = getOsmd();
        const cursor = osmd?.cursors?.[0];
        if (!osmd || !cursor) return;
        if (!note) {
            cursor.hide();
            return;
        }
        cursor.iterator = osmd.Sheet.MusicPartManager.getIterator(note.sourceNote.getAbsoluteTimestamp());
        cursor.show();
    }

    // Letzte Pattern-Suche: `highlightNotes` läuft bei jeder Hover-Bewegung über die Partitur
    // erneut (`restoreSelectionHighlight`), die Suche traversiert aber das ganze Stück. Gleiche
    // GraphicalNote-Instanzen in gleicher Reihenfolge ⇒ gleiches Ergebnis; ein Re-Render erzeugt
    // neue Instanzen und verwirft den Eintrag damit von selbst.
    let lastPatternInput: GraphicalNote[] = [];
    let lastPatternResult: GraphicalNote[] = [];

    function patternMatchesFor(notes: GraphicalNote[]): GraphicalNote[] {
        if (!findPatternMatches || notes.length < 2) return [];
        const same = notes.length === lastPatternInput.length
            && notes.every((n, i) => n === lastPatternInput[i]);
        if (!same) {
            lastPatternInput = [...notes];
            lastPatternResult = findPatternMatches(notes);
        }
        return lastPatternResult;
    }

    /** Färbt eine ganze Auswahl magenta ein (temporäres Highlight) und setzt den grünen Note-Cursor
     *  auf die `primary`-Note (zuletzt geklickt). Ersetzt jede vorherige Einfärbung (inkl. eines
     *  aktiven Hover-Layers). Für die Mehrfachauswahl: alle `notes` werden gefärbt, der Cursor
     *  markiert nur die primäre. Ab zwei Noten färbt es davor die Wiederholungen der Auswahl im
     *  Pattern-Layer – hier und nicht in einem eigenen Watch, weil jede Auswahländerung und jedes
     *  Re-Render durch diese Funktion laufen und die Layer-Reihenfolge so nicht reißen kann. */
    function highlightNotes(notes: GraphicalNote[], primary: GraphicalNote | null): void {
        clearSelectionColor();
        for (const n of patternMatchesFor(notes)) colorNote(n, PATTERN_COLOR, patternColoredNodes);
        for (const n of notes) applySelectionColor(n);
        positionNoteCursor(primary);
    }

    /** Positioniert den grünen Note-Cursor (cursors[0]) exakt auf die Note und färbt sie magenta.
     *  `null` versteckt den Cursor und entfernt das Farb-Highlight. (Die Synchronisation von
     *  `selectedMeasure` auf den Takt der Note übernimmt der Aufrufer – Selektion und
     *  Markierung bleiben so entkoppelt.) Einzelnoten-Fall von {@link highlightNotes}. */
    function highlightNote(note: GraphicalNote | null): void {
        highlightNotes(note ? [note] : [], note);
    }

    /** Pixel-Position (X/Y) der angeklickten Note relativ zum Scroll-Container der Vorschau
     *  (Anker des Editors im NoteActionsOverlay und des BassLyricPopover). Quelle ist die **graphische Note** (nicht der Klick),
     *  daher tracking-fest über Re-Render/Transpose/Zoom; Scroll wird per Listener nachgezogen.
     *  `null` ⇒ keine Note.
     *
     *  **Bezug ist das `<svg>` DER SEITE dieser Note, nicht der Container (kritisch – nicht
     *  rückbauen).** `AbsolutePosition` ist seiten-lokal (Herleitung in `osmd/pageSvgs.ts`); gegen
     *  den Container gerechnet landeten Editor und Tooltip bei Noten ab Seite 2 auf Seite 1. */
    function computeSelectedNotePosition(): void {
        const note = clickedGraphicalNote.value;
        const osmd = getOsmd();
        const container = containerRef.value;
        const scroller = scrollContainerRef.value;
        if (!note || !osmd || !container || !scroller) {
            selectedNoteX.value = null;
            selectedNoteY.value = null;
            return;
        }
        try {
            // Note-Position in OSMD-Einheiten → Client-Pixel über die Seite der Note; die
            // getBoundingClientRect()/CTM der Seite enthält bereits Scroll, Padding und Zoom.
            const pos = note.PositionAndShape.AbsolutePosition;
            const scrollerRect = scroller.getBoundingClientRect();
            const svgs = collectPageSvgs(container);
            const pageIndex = pageIndexOfNote(note);
            const svg = svgs[pageIndex >= 0 ? pageIndex : 0];
            if (svg) {
                const point = sheetUnitsToClient(svg, pos.x, pos.y, osmd.zoom);
                selectedNoteX.value = point.x - scrollerRect.left;
                selectedNoteY.value = point.y - scrollerRect.top;
            } else {
                // Kein gerendertes SVG (z. B. Test-Umgebung): Container-Rechnung wie bisher.
                const containerRect = container.getBoundingClientRect();
                const scale = unitInPixels * (osmd.zoom || 1);
                selectedNoteX.value = (containerRect.left + pos.x * scale) - scrollerRect.left;
                selectedNoteY.value = (containerRect.top + pos.y * scale) - scrollerRect.top;
            }
        } catch {
            selectedNoteX.value = null;
            selectedNoteY.value = null;
        }
    }

    return {
        highlightMeasure,
        clearHighlight,
        highlightNote,
        highlightNotes,
        highlightHoverNotes,
        highlightUnplayableNotes,
        clearHoverColor,
        clearSelectionColor,
        clearUnplayableColor,
        computeSelectedNotePosition,
    } as const;
}

export type ScoreHighlight = ReturnType<typeof useScoreHighlight>;
